// Pure entity-position resolver (Phase 5 Step 16a).
//
// Extracted from main.ts's ticker closure so the lookup precedence —
// expedition peer map > zone roster > mob map > null — is pinned by
// a unit test and can't silently regress when new position sources
// are added.
//
// Not a renderer dep — this is pure Map lookups with no PixiJS
// imports, safe to unit-test in the server test harness.

import type { PlayerState } from "../../shared/messages.ts";

export type EntityPosition = { x: number; y: number };

export type EntityPositionSources = {
  // Local player's live predicted position.
  readonly localPlayer: { readonly id: string; readonly x: number; readonly y: number };
  // Expedition-scope peers, refreshed by expeditionPositions broadcast.
  readonly expeditionPeers: ReadonlyMap<string, EntityPosition>;
  // Open-world roster fallback.
  readonly zonePlayers: ReadonlyMap<string, PlayerState>;
  // Mob positions, seeded by mobSpawned + refreshed by expeditionPositions.
  readonly mobPositions: ReadonlyMap<string, EntityPosition>;
};

// Precedence (first non-null wins):
//   1. local player — authoritative client-side prediction
//   2. expedition peer map — the ephemeral tick broadcast; stays current
//      while in expedition
//   3. zone roster — open-world fallback for peers outside expedition
//   4. mob position map
//   5. null — entity not yet known (snapshot will backfill)
export function resolveEntityPosition(
  sources: EntityPositionSources,
  entityId: string,
): EntityPosition | null {
  if (entityId === sources.localPlayer.id) {
    return { x: sources.localPlayer.x, y: sources.localPlayer.y };
  }
  const peer = sources.expeditionPeers.get(entityId);
  if (peer) return { x: peer.x, y: peer.y };
  const zonePlayer = sources.zonePlayers.get(entityId);
  if (zonePlayer) return { x: zonePlayer.x, y: zonePlayer.y };
  const mob = sources.mobPositions.get(entityId);
  if (mob) return { x: mob.x, y: mob.y };
  return null;
}
